/**
 * Canonical email-address pattern. Kept here in the utils (leaf) layer so it can
 * be reused everywhere without creating cross-layer import boundaries.
 *
 * This is the WHATWG/HTML5 `<input type="email">` validation pattern (the same
 * check browsers apply natively), with one tweak: the domain part uses `+`
 * instead of `*` so at least one dot is required — i.e. `user@localhost` is
 * rejected, matching our need for a fully-qualified domain.
 *
 * It accepts every valid address (tags, sub-domains, multi-level TLDs, punycode/
 * IDN domains) while rejecting common typos the older `[^\s@]` pattern let
 * through: trailing dots (`a@b.com.`), consecutive dots (`a@b..com`) and
 * hyphen-only domains (`a@-.-`).
 */
export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/**
 * Returns true when `value` is a valid email address.
 * Trims surrounding whitespace and treats empty/nullish values as invalid.
 */
export const isValidEmail = (value?: string | null): boolean =>
  EMAIL_REGEX.test(value?.trim() ?? '');

/* -------------------------------------------------------------------------- *
 * Legacy variant — pending unification onto `EMAIL_REGEX`.
 *
 * A pre-existing pattern still used by one consumer; centralized here (byte-for-
 * byte, so behavior is unchanged) so a future unification can happen in one
 * place. Prefer `EMAIL_REGEX` / `isValidEmail` for any NEW code.
 * -------------------------------------------------------------------------- */

/**
 * Dot-atom style pattern.
 * Used by: mco `send-preview`.
 */
export const EMAIL_REGEX_DOT_ATOM =
  /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
